/** @typedef {'pexels' | 'dreamvinyl' | 'other' | null} NoticiaImagenSource */

import {
  NOTICIA_IMAGEN_DELIVERY,
  NOTICIA_IMAGEN_PROVIDER,
  inferImagenProviderFromUrl,
  isPexelsUrl,
  isPollinationsUrl,
  normalizeImagenProvider,
  resolveNoticiaImagenDelivery,
  resolveNoticiaImagenProvider,
} from './noticiasImagenProviders.js';

export {
  NOTICIA_IMAGEN_DELIVERY,
  NOTICIA_IMAGEN_PROVIDER,
  getNoticiaImagenOrigenLabel,
  resolveNoticiaImagenDelivery,
  resolveNoticiaImagenProvider,
} from './noticiasImagenProviders.js';

export const NOTICIA_IMAGEN_SOURCE = {
  PEXELS: 'pexels',
  DREAMVINYL: 'dreamvinyl',
  OTHER: 'other',
};

/** Opciones de procedencia en admin (subida manual). */
export const NOTICIA_IMAGEN_PROCEDENCIA_OPTIONS = [
  { value: NOTICIA_IMAGEN_SOURCE.DREAMVINYL, label: 'DreamVinyl (propia o generada con IA)' },
  { value: NOTICIA_IMAGEN_SOURCE.PEXELS, label: 'Pexels' },
  { value: NOTICIA_IMAGEN_SOURCE.OTHER, label: 'Otra procedencia' },
];

const NOTICIAS_IMAGENES_BUCKET = 'noticias-imagenes';

function isOurStorageUrl(url) {
  const s = String(url ?? '').trim();
  if (!s) return false;
  try {
    const u = new URL(s.startsWith('//') ? `https:${s}` : s);
    return u.pathname.includes(`/object/public/${NOTICIAS_IMAGENES_BUCKET}/`);
  } catch {
    return false;
  }
}

function normalizeSource(raw) {
  const provider = normalizeImagenProvider(raw);
  if (provider === NOTICIA_IMAGEN_PROVIDER.PEXELS) return NOTICIA_IMAGEN_SOURCE.PEXELS;
  if (provider === NOTICIA_IMAGEN_PROVIDER.OTHER) return NOTICIA_IMAGEN_SOURCE.OTHER;
  if (provider) return NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
  return null;
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

const flatAttributionKeys = [
  'attribution_text',
  'credit_text',
  'imagen_attribution',
  'imagen_procedencia_text',
];

function readAttributionText(base, imagen) {
  for (const key of flatAttributionKeys) {
    const v = imagen[key] ?? base[key];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

/** Texto visible para procedencia «otra». */
export function formatNoticiaImagenOtherAttribution(text) {
  const t = String(text ?? '').trim();
  if (!t) return 'Imagen de fuente externa';
  if (/^imagen\s+de\s+/i.test(t)) return t;
  return `Imagen de ${t}`;
}

/** Normaliza campos planos que puede enviar n8n (Pexels) al objeto metadata.imagen. */
export function normalizeNoticiaImagenMetadata(metadata) {
  const base = asObject(metadata);
  const imagen = { ...asObject(base.imagen) };

  const flatSource = base.imagen_source ?? base.image_source ?? base.imagenSource ?? base.image_provider;
  const provider = resolveNoticiaImagenProvider(base, null);
  if (provider && !imagen.provider) {
    imagen.provider = provider;
  }

  if (!imagen.source && flatSource) {
    imagen.source = normalizeSource(flatSource);
  } else if (imagen.source) {
    imagen.source = normalizeSource(imagen.source) ?? imagen.source;
  }

  if (!imagen.source && (base.imagen_base64 || base.imagenBase64 || imagen.imagen_base64)) {
    imagen.source = NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
  }

  const pexelsUrl =
    imagen.pexels_url ??
    base.pexels_url ??
    base.pexels_photo_url ??
    base.imagen_pexels_url;
  if (pexelsUrl && !imagen.pexels_url) {
    imagen.pexels_url = String(pexelsUrl).trim();
  }

  const originalUrl = imagen.original_url ?? base.imagen_original_url ?? base.original_imagen_url;
  if (originalUrl && !imagen.original_url) {
    imagen.original_url = String(originalUrl).trim();
  }

  const attribution = readAttributionText(base, imagen);
  if (attribution && !imagen.attribution_text) {
    imagen.attribution_text = attribution;
  }

  const flatBase64 = base.imagen_base64 ?? base.imagenBase64 ?? imagen.imagen_base64;
  if (flatBase64 && !imagen.pending_base64) {
    imagen.pending_base64 = true;
  }

  return { ...base, imagen };
}

/**
 * Lee procedencia guardada en metadata (admin / edición).
 * @param {Record<string, unknown>|null|undefined} metadata
 */
export function getNoticiaImagenProcedencia(metadata) {
  const meta = normalizeNoticiaImagenMetadata(metadata);
  const imagen = asObject(meta.imagen);
  const source = normalizeSource(imagen.source) ?? NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
  return {
    source,
    attributionText: readAttributionText(meta, imagen),
    pexelsUrl: String(imagen.pexels_url ?? '').trim() || 'https://www.pexels.com/',
  };
}

/**
 * Aplica procedencia elegida en admin (subida o edición).
 * @param {Record<string, unknown>|null|undefined} metadata
 * @param {string|null|undefined} imagenUrl
 * @param {{ source?: string, attributionText?: string, pexelsUrl?: string }} procedencia
 */
export function applyNoticiaImagenProcedencia(metadata, imagenUrl, procedencia = {}) {
  const normalized = normalizeNoticiaImagenMetadata(metadata);
  const url = String(imagenUrl ?? '').trim();
  const imagen = { ...asObject(normalized.imagen) };
  const src = normalizeSource(procedencia.source) ?? NOTICIA_IMAGEN_SOURCE.DREAMVINYL;

  if (!url) {
    delete normalized.imagen;
    return normalized;
  }

  imagen.source = src;

  if (src === NOTICIA_IMAGEN_SOURCE.PEXELS) {
    imagen.pexels_url = String(procedencia.pexelsUrl ?? '').trim() || 'https://www.pexels.com/';
    delete imagen.attribution_text;
  } else if (src === NOTICIA_IMAGEN_SOURCE.OTHER) {
    const text = String(procedencia.attributionText ?? '').trim();
    if (text) imagen.attribution_text = text;
    else delete imagen.attribution_text;
    delete imagen.pexels_url;
  } else {
    delete imagen.pexels_url;
    delete imagen.attribution_text;
  }

  normalized.imagen = imagen;
  return normalized;
}

/** @returns {NoticiaImagenSource} */
export function inferNoticiaImagenSourceFromUrl(url) {
  const s = String(url ?? '').trim();
  if (!s) return null;
  const provider = inferImagenProviderFromUrl(s);
  if (provider === NOTICIA_IMAGEN_PROVIDER.PEXELS) return NOTICIA_IMAGEN_SOURCE.PEXELS;
  if (isOurStorageUrl(s) || isPollinationsUrl(s) || provider === NOTICIA_IMAGEN_PROVIDER.POLLINATIONS) {
    return NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
  }
  return NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
}

/**
 * Actualiza metadata.imagen según la URL actual (borrador, edición admin o publish).
 * @param {Record<string, unknown>|null|undefined} metadata
 * @param {string|null|undefined} imagenUrl
 */
export function mergeNoticiaImagenMetadata(metadata, imagenUrl) {
  const normalized = normalizeNoticiaImagenMetadata(metadata);
  const url = String(imagenUrl ?? '').trim();
  const imagen = { ...asObject(normalized.imagen) };
  const explicit = normalizeSource(imagen.source);

  if (!url) {
    delete normalized.imagen;
    return normalized;
  }

  if (isPexelsUrl(url)) {
    imagen.source = NOTICIA_IMAGEN_SOURCE.PEXELS;
    imagen.original_url = url;
    if (!imagen.provider) imagen.provider = NOTICIA_IMAGEN_PROVIDER.PEXELS;
  } else if (explicit === NOTICIA_IMAGEN_SOURCE.PEXELS && isOurStorageUrl(url)) {
    // Publicada: imagen copiada a Storage; conservar crédito Pexels.
  } else if (explicit === NOTICIA_IMAGEN_SOURCE.OTHER) {
    // Procedencia externa manual; conservar attribution_text.
  } else {
    imagen.source = NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
    if (!isOurStorageUrl(url)) imagen.original_url = url;
    if (!imagen.provider) {
      imagen.provider = resolveNoticiaImagenProvider(normalized, url) ?? NOTICIA_IMAGEN_PROVIDER.DREAMVINYL;
    }
    delete imagen.pexels_url;
    delete imagen.attribution_text;
  }

  normalized.imagen = imagen;
  return normalized;
}

/**
 * @param {{ imagen_url?: string|null, metadata?: Record<string, unknown>|null }} row
 * @returns {{ type: 'pexels'|'dreamvinyl'|'other'|null, pexelsUrl: string|null, attributionText: string|null }}
 */
export function resolveNoticiaImagenCredit(row) {
  const delivery = resolveNoticiaImagenDelivery(row);
  if (delivery === NOTICIA_IMAGEN_DELIVERY.NONE) {
    return { type: null, pexelsUrl: null, attributionText: null };
  }

  const imagenUrl = String(row?.imagen_url ?? '').trim();
  const meta = normalizeNoticiaImagenMetadata(row?.metadata);
  const imagen = asObject(meta.imagen);
  const originalUrl = String(imagen.original_url ?? imagenUrl).trim();

  let source = normalizeSource(imagen.source);
  if (!source) {
    const provider = resolveNoticiaImagenProvider(meta, imagenUrl);
    if (provider === NOTICIA_IMAGEN_PROVIDER.PEXELS) source = NOTICIA_IMAGEN_SOURCE.PEXELS;
    else if (provider === NOTICIA_IMAGEN_PROVIDER.OTHER) source = NOTICIA_IMAGEN_SOURCE.OTHER;
    else source =
      inferNoticiaImagenSourceFromUrl(originalUrl) ??
      inferNoticiaImagenSourceFromUrl(imagenUrl) ??
      NOTICIA_IMAGEN_SOURCE.DREAMVINYL;
  }

  if (source === NOTICIA_IMAGEN_SOURCE.OTHER) {
    return {
      type: 'other',
      pexelsUrl: null,
      attributionText: readAttributionText(meta, imagen) || null,
    };
  }

  if (source === NOTICIA_IMAGEN_SOURCE.PEXELS || isPexelsUrl(originalUrl) || isPexelsUrl(imagenUrl)) {
    const pexelsUrl =
      String(imagen.pexels_url ?? '').trim() ||
      (isPexelsUrl(originalUrl) ? originalUrl : isPexelsUrl(imagenUrl) ? imagenUrl : 'https://www.pexels.com/');
    return { type: 'pexels', pexelsUrl, attributionText: null };
  }

  return { type: 'dreamvinyl', pexelsUrl: null, attributionText: null };
}
